import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import styled from 'styled-components';

// Use the environment variable for the Stripe publishable key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
  console.error('Stripe publishable key is missing. Please check your .env file.');
}
const stripePromise = loadStripe(stripeKey || '');

const packages = [
  {
    id: 'basic',
    name: 'Basic Package',
    price: 49.99,
    features: ['Access to basic course content', 'Community forum access', 'Basic support'],
  },
  {
    id: 'premium',
    name: 'Premium Package',
    price: 99.99,
    features: ['All basic features', 'Advanced course content', 'Priority support', '1-on-1 mentoring'],
  },
];

const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
`;

const LoginButton = styled(Link)`
  background-color: #4299e1;
  color: white;
  padding: 0.5rem 1.25rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: background 0.2s;
  &:hover {
    background-color: #3182ce;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #666;
`;

const PackageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const PackageCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }
`;

const PackageTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 1rem;
`;

const Price = styled.div`
  font-size: 2rem;
  color: #2c5282;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  margin-bottom: 0.5rem;
  color: #4a5568;
  &:before {
    content: "✓";
    color: #48bb78;
    margin-right: 0.5rem;
  }
`;

const PayButton = styled.button`
  background-color: #2c5282;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2b6cb0;
  }

  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);

  const handlePayment = async (packageId: string, amount: number) => {
    try {
      setLoading(packageId);
      console.log('Starting payment process...');
      
      const stripe = await stripePromise;
      if (!stripe) {
        console.error('Stripe failed to initialize');
        throw new Error('Stripe failed to initialize');
      }

      console.log('Creating checkout session...');
      // In a real application, you would make an API call to your backend
      // to create a Checkout Session. For demo purposes, we'll use a mock session.
      const { error } = await stripe.redirectToCheckout({
        lineItems: [
          {
            price: 'price_1RSf9nFrDsGkllM6y37seEDQ', // This should be your Stripe Price ID
            quantity: 1,
          },
        ],
        mode: 'payment',
        successUrl: `${window.location.origin}/dashboard`,
        cancelUrl: `${window.location.origin}/`,
      });

      if (error) {
        console.error('Stripe error:', error);
        alert(`Payment failed: ${error.message}`);
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <HomeContainer>
      <TopBar>
        <LoginButton to="/login">Login</LoginButton>
      </TopBar>
      <Header>
        <Title>Welcome to Our Course Platform</Title>
        <Subtitle>Choose a package to get started with your learning journey</Subtitle>
      </Header>

      <PackageGrid>
        {packages.map((pkg) => (
          <PackageCard key={pkg.id}>
            <PackageTitle>{pkg.name}</PackageTitle>
            <Price>${pkg.price}</Price>
            <FeatureList>
              {pkg.features.map((feature, index) => (
                <FeatureItem key={index}>{feature}</FeatureItem>
              ))}
            </FeatureList>
            <PayButton 
              onClick={() => handlePayment(pkg.id, pkg.price)}
              disabled={loading === pkg.id}
            >
              {loading === pkg.id ? 'Processing...' : `Pay $${pkg.price}`}
            </PayButton>
          </PackageCard>
        ))}
      </PackageGrid>
    </HomeContainer>
  );
};

export default HomePage; 